import React from "react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { AuthProvider } from "../hooks/useAuth";
import { Routes } from "./Routes";

// Augment MUI's Palette to include the custom "brand" color
declare module "@mui/material/styles" {
  interface Palette {
    brand: Palette["primary"];
  }
  interface PaletteOptions {
    brand?: PaletteOptions["primary"];
  }
}
// Allow color="brand" on MUI Button
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    brand: true;
  }
}

const theme = createTheme({
  typography: {
    fontFamily: ["Poppins", "sans-serif"].join(","),
  },
  palette: {
    primary:   { main: "#406896", dark: "#314e7c", light: "#7b98b7", contrastText: "#fff" },
    secondary: { main: "#E4E6EF" },
    error:     { main: "#8f4242", dark: "#6d3131", light: "#f2e0e0", contrastText: "#fff" },
    warning:   { main: "#906631", dark: "#6e4e26", light: "#f5ede0", contrastText: "#fff" },
    success:   { main: "#385936", dark: "#2b442a", light: "#e4ede3", contrastText: "#fff" },
    info:      { main: "#306b8d", dark: "#244f6b", light: "#deeaf1", contrastText: "#fff" },
    brand:     { main: "#E77575", dark: "#d45f5f", light: "#f5c6c6", contrastText: "#fff" },
    background: {
      default: "#EEF0F8",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#3F4254",
      secondary: "#B5B5C3",
    },
  },
  components: {
    MuiButtonBase: { defaultProps: { disableRipple: false } },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none" as const,
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: { color: "#3F4254" },
      },
    },
    MuiPopover: { defaultProps: { elevation: 1 } },
  },
});

export default function App() {
  return (
    <React.Suspense fallback={<Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}><CircularProgress /></Box>}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <Routes />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </React.Suspense>
  );
}
